/**
 * MT-1 AUTH-009 (§3.9, R2-F5): the platform console's own idle clock, shared by its tabs through
 * localStorage under its OWN keys. The school clock (`session-activity.ts`) and this one never
 * read, reset or clear each other.
 */
export const PLATFORM_LAST_ACTIVITY_KEY = "lms_platform_last_activity";
/** An idle limit for the platform console, when one was reported; else the default applies. */
export const PLATFORM_IDLE_LIMIT_KEY = "lms_platform_idle_limit_ms";
/** Written when a tab signs the platform out, so the other platform tabs follow. */
export const PLATFORM_SIGNOUT_KEY = "lms_platform_signout";

const WRITE_THROTTLE_MS = 5_000;

function storage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

export function readPlatformLastActivity(): number | null {
  let raw: string | null | undefined;
  try {
    raw = storage()?.getItem(PLATFORM_LAST_ACTIVITY_KEY);
  } catch {
    return null;
  }
  const value = raw ? Number.parseInt(raw, 10) : Number.NaN;
  return Number.isFinite(value) && value > 0 ? value : null;
}

/** Marks platform activity now (throttled unless forced). */
export function touchPlatformActivity(
  now: number = Date.now(),
  options: { force?: boolean } = {},
): void {
  if (!options.force) {
    const last = readPlatformLastActivity();
    if (last !== null && now >= last && now - last < WRITE_THROTTLE_MS) return;
  }
  try {
    storage()?.setItem(PLATFORM_LAST_ACTIVITY_KEY, String(now));
  } catch {
    // Storage blocked: the clock then sees no activity.
  }
}

export function clearPlatformActivity(): void {
  try {
    storage()?.removeItem(PLATFORM_LAST_ACTIVITY_KEY);
  } catch {
    // ignore
  }
}

/** A stored platform idle limit (ms), when a valid one is there. */
export function readPlatformIdleLimit(minMs: number): number | null {
  try {
    const value = Number.parseInt(storage()?.getItem(PLATFORM_IDLE_LIMIT_KEY) ?? "", 10);
    return Number.isFinite(value) && value >= minMs ? value : null;
  } catch {
    return null;
  }
}

export function broadcastPlatformSignOut(reason: "idle" | "logout"): void {
  try {
    storage()?.setItem(PLATFORM_SIGNOUT_KEY, JSON.stringify({ reason, at: Date.now() }));
  } catch {
    // ignore
  }
}

/** Runs `onSignOut` when another tab signs the platform out. Returns the unsubscribe. */
export function onOtherTabPlatformSignOut(onSignOut: () => void): () => void {
  if (typeof window === "undefined") return () => undefined;
  const listener = (event: StorageEvent) => {
    if (event.key === PLATFORM_SIGNOUT_KEY && event.newValue) onSignOut();
  };
  window.addEventListener("storage", listener);
  return () => window.removeEventListener("storage", listener);
}
